export const DEFAULT_BRANCH_PAYMENT_PROVIDER = "manual";
export const DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS = "not_configured";
export const DEFAULT_BRANCH_PAYMENT_SPLIT_MODE =
  "mercado_pago_marketplace_fee";
export const DEFAULT_BRANCH_COMMISSION_RATE = 0.006;
export const DEFAULT_MENU_QR_COMMISSION_RATE = DEFAULT_BRANCH_COMMISSION_RATE;
export const DEFAULT_KIOSK_COMMISSION_RATE = DEFAULT_BRANCH_COMMISSION_RATE;
export const DEFAULT_IA_COMMISSION_RATE = DEFAULT_BRANCH_COMMISSION_RATE;
export const DEFAULT_MANUAL_OPERATIONAL_MZ_COST_UNITS = 8;
export const PAYMENT_SPLIT_MODES = Object.freeze({
  COLLECT_ON_NEXT_DIGITAL: "collect_on_next_digital",
  MERCADO_PAGO_MARKETPLACE_FEE: "mercado_pago_marketplace_fee",
});
export const DEFAULT_KIOSK_PAYMENT_METHODS = ["pay_at_counter", "pay_by_phone"];
export const DEFAULT_QR_PAYMENT_METHODS = ["pay_later", "pay_now"];
export const KIOSK_COUNTER_PAYMENT_METHOD = "pay_at_counter";
export const KIOSK_DIGITAL_PAYMENT_METHOD = "pay_by_phone";

const PAYMENT_PROVIDER_OPTIONS = new Set([
  "manual",
  "mercado_pago",
  "modo",
  "uala",
  "naranja_x",
]);
const DIGITAL_PROVIDER_OPTIONS = new Set([
  "mercado_pago",
  "modo",
  "uala",
  "naranja_x",
]);
const PAYMENT_PROVIDER_STATUS_OPTIONS = new Set([
  "not_configured",
  "pending",
  "connected",
  "restricted",
]);
const PAYMENT_SPLIT_MODE_OPTIONS = new Set([
  PAYMENT_SPLIT_MODES.COLLECT_ON_NEXT_DIGITAL,
  PAYMENT_SPLIT_MODES.MERCADO_PAGO_MARKETPLACE_FEE,
]);
const KIOSK_PAYMENT_METHOD_OPTIONS = new Set(["pay_at_counter", "pay_by_phone"]);
const QR_PAYMENT_METHOD_OPTIONS = new Set(["pay_later", "pay_now"]);

const PAYMENT_SPLIT_MODE_LABELS = {
  [PAYMENT_SPLIT_MODES.COLLECT_ON_NEXT_DIGITAL]:
    "Cobrar comision en el siguiente pago digital",
  [PAYMENT_SPLIT_MODES.MERCADO_PAGO_MARKETPLACE_FEE]:
    "Marketplace split inmediato con Mercado Pago",
};

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);
const toWholeUnits = (value, fallback = 0) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return Math.max(0, Math.round(fallback || 0));
  }

  return Math.max(0, Math.round(parsed));
};

const sanitizePaymentAccountReference = (value) => {
  const normalized = toSafeText(value, 160);
  return /^APP_USR-/i.test(normalized) ? "" : normalized;
};

const toBoolean = (value) => value === true;
const hasStreetNumber = (value) => /\d/.test(toSafeText(value, 200));
const hasCoordinate = (value) => {
  const normalized = Number(`${value ?? ""}`.trim().replace(",", "."));
  return Number.isFinite(normalized);
};

export const normalizePaymentProvider = (value) => {
  const normalized = toSafeText(value, 40).toLowerCase();
  return PAYMENT_PROVIDER_OPTIONS.has(normalized)
    ? normalized
    : DEFAULT_BRANCH_PAYMENT_PROVIDER;
};

export const normalizePaymentProviderStatus = (value) => {
  const normalized = toSafeText(value, 40).toLowerCase();
  return PAYMENT_PROVIDER_STATUS_OPTIONS.has(normalized)
    ? normalized
    : DEFAULT_BRANCH_PAYMENT_PROVIDER_STATUS;
};

export const normalizePaymentSplitMode = (value) => {
  const normalized = toSafeText(value, 40).toLowerCase();
  return PAYMENT_SPLIT_MODE_OPTIONS.has(normalized)
    ? normalized
    : DEFAULT_BRANCH_PAYMENT_SPLIT_MODE;
};

export const normalizeCommissionRate = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_BRANCH_COMMISSION_RATE;
  }

  return Math.min(0.25, Math.max(0, Math.round(parsed * 10000) / 10000));
};

export const normalizePaymentMethods = (value, channel = "kiosk") => {
  const source = Array.isArray(value) ? value : [];
  const allowedOptions =
    channel === "qr" ? QR_PAYMENT_METHOD_OPTIONS : KIOSK_PAYMENT_METHOD_OPTIONS;
  const fallback =
    channel === "qr" ? DEFAULT_QR_PAYMENT_METHODS : DEFAULT_KIOSK_PAYMENT_METHODS;

  const normalized = source
    .map((entry) => toSafeText(entry, 40).toLowerCase())
    .filter((entry, index, collection) => {
      return allowedOptions.has(entry) && collection.indexOf(entry) === index;
    });

  return normalized.length > 0 ? normalized : [...fallback];
};

export const getPaymentSplitModeLabel = (value) =>
  PAYMENT_SPLIT_MODE_LABELS[normalizePaymentSplitMode(value)] ||
  PAYMENT_SPLIT_MODE_LABELS[DEFAULT_BRANCH_PAYMENT_SPLIT_MODE];

export const resolveBranchChannelCommissionRates = (source = {}) => {
  const manualOrderCommissionRate = normalizeCommissionRate(
    source?.manualOrderCommissionRate ??
      source?.manual_order_commission_rate ??
      source?.commissionRate ??
      source?.commission_rate
  );
  const menuQrCommissionRate = normalizeCommissionRate(
    source?.menuQrCommissionRate ??
      source?.menu_qr_commission_rate ??
      DEFAULT_MENU_QR_COMMISSION_RATE
  );
  const kioskCommissionRate = normalizeCommissionRate(
    source?.kioskCommissionRate ??
      source?.kiosk_commission_rate ??
      DEFAULT_KIOSK_COMMISSION_RATE
  );
  const iaCommissionRate = normalizeCommissionRate(
    source?.iaCommissionRate ??
      source?.ia_commission_rate ??
      DEFAULT_IA_COMMISSION_RATE
  );

  return {
    manualOrderCommissionRate,
    menuQrCommissionRate,
    kioskCommissionRate,
    iaCommissionRate,
  };
};

export const resolveBranchCommissionRateByChannel = (
  source = {},
  channel = "manual"
) => {
  const rates = resolveBranchChannelCommissionRates(source);

  if (channel === "menu_qr") {
    return rates.menuQrCommissionRate;
  }

  if (channel === "kiosk") {
    return rates.kioskCommissionRate;
  }

  if (channel === "ia") {
    return rates.iaCommissionRate;
  }

  return rates.manualOrderCommissionRate;
};

export const isDigitalPaymentProvider = (value) =>
  DIGITAL_PROVIDER_OPTIONS.has(normalizePaymentProvider(value));

export const supportsKioskDigitalProvider = (value) =>
  normalizePaymentProvider(value) === "mercado_pago";

export const supportsMenuQrDigitalProvider = (value) =>
  normalizePaymentProvider(value) === "mercado_pago";

export const buildBranchPaymentSetupSnapshot = (source = {}) => {
  const paymentProvider = normalizePaymentProvider(source?.paymentProvider);
  const paymentProviderStatus = normalizePaymentProviderStatus(
    source?.paymentProviderStatus
  );
  const paymentSplitMode = normalizePaymentSplitMode(source?.paymentSplitMode);
  const {
    manualOrderCommissionRate,
    menuQrCommissionRate,
    kioskCommissionRate,
    iaCommissionRate,
  } = resolveBranchChannelCommissionRates(source);
  const kioskPaymentMethods = normalizePaymentMethods(
    source?.kioskPaymentMethods,
    "kiosk"
  );
  const qrPaymentMethods = normalizePaymentMethods(source?.qrPaymentMethods, "qr");
  const paymentAccountReference = sanitizePaymentAccountReference(
    source?.paymentAccountReference
  );
  const paymentAccountCbu = toSafeText(source?.paymentAccountCbu, 40);
  const paymentAccountAlias = toSafeText(source?.paymentAccountAlias, 80);
  const paymentLocationCity = toSafeText(source?.paymentLocationCity, 120);
  const paymentLocationState = toSafeText(source?.paymentLocationState, 120);
  const paymentLocationLatitude = toSafeText(source?.paymentLocationLatitude, 40);
  const paymentLocationLongitude = toSafeText(source?.paymentLocationLongitude, 40);
  const paymentLocationReference = toSafeText(source?.paymentLocationReference, 240);
  const commissionRate = manualOrderCommissionRate;
  const kioskCounterEnabled = kioskPaymentMethods.includes(
    KIOSK_COUNTER_PAYMENT_METHOD
  );
  const kioskDigitalEnabled = kioskPaymentMethods.includes(
    KIOSK_DIGITAL_PAYMENT_METHOD
  );
  const qrPayLaterEnabled = qrPaymentMethods.includes("pay_later");
  const qrDigitalEnabled = qrPaymentMethods.includes("pay_now");
  const providerSupportsKioskDigital =
    supportsKioskDigitalProvider(paymentProvider);
  const providerSupportsMenuQrDigital =
    supportsMenuQrDigitalProvider(paymentProvider);
  const providerSupportsDigital =
    providerSupportsKioskDigital || providerSupportsMenuQrDigital;
  const hasSettlementAccount = Boolean(paymentAccountReference || paymentAccountAlias || paymentAccountCbu);
  const providerConnected = paymentProviderStatus === "connected";
  const hasProvisioningAddress = Boolean(
    hasStreetNumber(source?.address) && paymentLocationCity && paymentLocationState
  );
  const hasProvisioningCoordinates =
    hasCoordinate(paymentLocationLatitude) && hasCoordinate(paymentLocationLongitude);
  const kioskProvisioningLocationReady =
    providerSupportsKioskDigital &&
    hasProvisioningAddress &&
    hasProvisioningCoordinates;
  const kioskDigitalConfigured =
    kioskDigitalEnabled &&
    providerSupportsKioskDigital &&
    providerConnected &&
    hasSettlementAccount;
  const qrDigitalConfigured =
    qrDigitalEnabled &&
    providerSupportsMenuQrDigital &&
    providerConnected;
  const kioskCanAutoProvision =
    kioskDigitalConfigured && kioskProvisioningLocationReady;
  const kioskNativeQrReady =
    kioskCanAutoProvision && toBoolean(source?.kioskNativeQrReady);
  const kioskQrReady = kioskDigitalConfigured;
  const manualOperationsRuleEnabled =
    source?.manualOperationsRuleEnabled !== false;
  const manualOperationUnitsCost = manualOperationsRuleEnabled
    ? toWholeUnits(
        source?.manualOperationUnitsCost,
        DEFAULT_MANUAL_OPERATIONAL_MZ_COST_UNITS
      )
    : 0;
  const manualOperationsAvailable =
    source?.manualOperationsAvailable === undefined
      ? true
      : source?.manualOperationsAvailable === true;
  const manualOperationsLowBalance =
    source?.manualOperationsLowBalance === true;
  const operationalAccessAllowed =
    source?.operationalAccessAllowed === undefined
      ? manualOperationsAvailable
      : source?.operationalAccessAllowed === true;
  const operationalAccessMode =
    toSafeText(source?.operationalAccessMode, 40) ||
    (manualOperationsAvailable
      ? providerConnected
        ? "hybrid"
        : "credits_only"
      : "blocked");

  let kioskQrBlockingReason = "";
  let qrDeepLinkBlockingReason = "";
  let manualOperationsBlockingReason = toSafeText(
    source?.manualOperationsBlockingReason,
    240
  );
  let operationalBlockingReason = toSafeText(
    source?.operationalBlockingReason,
    240
  );

  if (!kioskDigitalEnabled) {
    kioskQrBlockingReason = "Activa 'Pagar con Mercado Pago' para el kiosk.";
  } else if (!providerSupportsKioskDigital) {
    kioskQrBlockingReason =
      paymentProvider === "modo"
        ? "Los cobros digitales de MozzoApp hoy operan con Mercado Pago. MODO queda reservado para la siguiente integracion interoperable."
        : "Los cobros digitales del kiosk se habilitan conectando la cuenta Mercado Pago del restaurante a MozzoApp.";
  } else if (!providerConnected) {
    kioskQrBlockingReason =
      paymentProviderStatus === "restricted"
        ? "La cuenta Mercado Pago del restaurante esta restringida y no puede cobrar desde el totem."
        : "La cuenta Mercado Pago del restaurante todavia no esta conectada a MozzoApp para cobrar desde el totem.";
  } else if (!hasSettlementAccount) {
    kioskQrBlockingReason =
      "Carga al menos una referencia, alias o CBU para dejar lista la liquidacion de la sucursal.";
  }

  if (!qrDigitalEnabled) {
    qrDeepLinkBlockingReason =
      "Activa 'Pagar ahora' en Menu QR para cobrar digitalmente desde la mesa.";
  } else if (!providerSupportsMenuQrDigital) {
    qrDeepLinkBlockingReason =
      paymentProvider === "modo"
        ? "Los cobros digitales de MozzoApp hoy operan con Mercado Pago. MODO queda reservado para la siguiente integracion interoperable."
        : "Los cobros digitales de Menu QR se habilitan conectando la cuenta Mercado Pago del restaurante a MozzoApp.";
  } else if (!providerConnected) {
    qrDeepLinkBlockingReason =
      paymentProviderStatus === "restricted"
        ? "La cuenta Mercado Pago del restaurante esta restringida y no puede cobrar desde el Menu QR."
        : "La cuenta Mercado Pago del restaurante todavia no esta conectada a MozzoApp para cobrar desde el Menu QR.";
  }

  if (!manualOperationsBlockingReason && !manualOperationsAvailable) {
    manualOperationsBlockingReason =
      "La cuenta se quedo sin creditos MZ. Necesita comprar un pack MZ para volver a operar.";
  }

  if (!operationalBlockingReason && !operationalAccessAllowed) {
    operationalBlockingReason =
      "Cuenta bloqueada por falta de creditos MZ. Compra un pack MZ para reactivar la sucursal.";
  }

  return {
    paymentProvider,
    paymentProviderStatus,
    paymentSplitMode,
    paymentAccountReference,
    paymentAccountCbu,
    paymentAccountAlias,
    commissionRate,
    manualOrderCommissionRate,
    menuQrCommissionRate,
    kioskCommissionRate,
    iaCommissionRate,
    kioskPaymentMethods,
    qrPaymentMethods,
    kioskCounterEnabled,
    kioskDigitalEnabled,
    qrPayLaterEnabled,
    qrDigitalEnabled,
    providerSupportsDigital,
    providerConnected,
    hasSettlementAccount,
    paymentLocationCity,
    paymentLocationState,
    paymentLocationLatitude,
    paymentLocationLongitude,
    paymentLocationReference,
    kioskDigitalConfigured,
    qrDigitalConfigured,
    kioskProvisioningLocationReady,
    kioskCanAutoProvision,
    kioskNativeQrReady,
    kioskQrReady,
    kioskQrBlockingReason,
    qrDeepLinkBlockingReason,
    manualOperationsRuleEnabled,
    manualOperationUnitsCost,
    manualOperationsAvailable,
    manualOperationsLowBalance,
    manualOperationsBlockingReason,
    operationalAccessAllowed,
    operationalAccessMode,
    operationalBlockingReason,
  };
};
